import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './VocabModes.module.css';
import { useUser } from '../../context/UserContext';

const VocabModes = () => {
  const { userProgress } = useUser();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('modes');
  const [vocabularyStats, setVocabularyStats] = useState(null);
  const [vocabularyLists, setVocabularyLists] = useState([]);


  const handleModeSelect = (mode, subMode) => {
    navigate(`/vocabulary/${mode}/${subMode}`, { 
      state: { 
        mode, 
        subMode,
        difficulty: userProgress?.vocabularyStats?.currentLevel || 4
      } 
    });
  };

  const handleCreateList = () => {
    navigate('/vocabulary/lists/create');
  };

  const handleViewList = (listId) => {
    navigate(`/vocabulary/lists/${listId}`);
  };

  const renderModes = () => {
    return (
      <div className={styles.modesContainer}>
        <div className={styles.modeSection}>
          <h3 className={styles.sectionTitle}>Learn & Practice</h3>
          <div className={styles.modeCards}>
            <div 
              className={styles.modeCard}
              onClick={() => handleModeSelect('learn_practice', 'flashcards')}
            >
              <div className={styles.modeIcon}>🔤</div>
              <h4>Flashcards</h4>
              <p>Learn and practice with interactive flashcards</p>
            </div>
            
            <div 
              className={styles.modeCard}
              onClick={() => handleModeSelect('learn_practice', 'sentence_usage')}
            >
              <div className={styles.modeIcon}>📝</div>
              <h4>Sentence Usage</h4>
              <p>Practice using words in sentences</p>
            </div>
            
            <div 
              className={styles.modeCard}
              onClick={() => handleModeSelect('learn_practice', 'word_association')}
            >
              <div className={styles.modeIcon}>🔄</div>
              <h4>Word Association</h4>
              <p>Connect words in meaningful sentences</p>
            </div>
          </div>
        </div>
        
        <div className={styles.modeSection}>
          <h3 className={styles.sectionTitle}>Memory Techniques</h3>
          <div className={styles.modeCards}>
            <div 
              className={styles.modeCard}
              onClick={() => handleModeSelect('memory', 'memory_palace')}
            >
              <div className={styles.modeIcon}>🏛️</div>
              <h4>Memory Palace</h4>
              <p>Visualize words in locations to remember them</p>
            </div>
            
            <div 
              className={styles.modeCard}
              onClick={() => handleModeSelect('memory', 'sound_mnemonics')}
            >
              <div className={styles.modeIcon}>🔊</div>
              <h4>Sound Mnemonics</h4>
              <p>Use sound associations to remember words</p>
            </div>
            
            <div 
              className={styles.modeCard}
              onClick={() => handleModeSelect('memory', 'chunking')}
            >
              <div className={styles.modeIcon}>🧩</div>
              <h4>Chunking</h4>
              <p>Group words by themes and connections</p>
            </div>
          </div>
        </div>
        
        <div className={styles.modeSection}>
          <h3 className={styles.sectionTitle}>Speaking & Writing</h3>
          <div className={styles.modeCards}>
            <div 
              className={styles.modeCard}
              onClick={() => handleModeSelect('speaking_listening', 'pronunciation_feedback')}
            >
              <div className={styles.modeIcon}>🎙️</div>
              <h4>Pronunciation</h4>
              <p>Get feedback on your pronunciation</p>
            </div>
            
            <div 
              className={styles.modeCard}
              onClick={() => handleModeSelect('writing', 'writing_prompts')}
            >
              <div className={styles.modeIcon}>✏️</div>
              <h4>Writing Prompts</h4>
              <p>Practice vocabulary in creative writing</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStats = () => {
    if (!vocabularyStats) {
      return <div className={styles.emptyState}>No statistics available yet.</div>;
    }
    
    return (
      <div className={styles.statsContainer}>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <h4>Words Learned</h4>
            <div className={styles.statValue}>{vocabularyStats.wordsLearned}</div>
          </div>
          
          <div className={styles.statCard}>
            <h4>Retention Rate</h4>
            <div className={styles.statValue}>{vocabularyStats.retentionRate}%</div>
          </div>
          
          <div className={styles.statCard}>
            <h4>Daily Streak</h4>
            <div className={styles.statValue}>{vocabularyStats.dailyStreak} days</div>
          </div>
        </div>
        
        <h3 className={styles.sectionTitle}>Recent Sessions</h3>
        {vocabularyStats.recentSessions && vocabularyStats.recentSessions.length > 0 ? (
          <div className={styles.sessionsTable}>
            <div className={styles.tableHeader}>
              <div>Date</div>
              <div>Mode</div>
              <div>Accuracy</div>
              <div>Words</div>
            </div>
            {vocabularyStats.recentSessions.map(session => (
              <div key={session.id} className={styles.tableRow}>
                <div>{new Date(session.date).toLocaleDateString()}</div>
                <div>{session.mode}</div>
                <div>{session.accuracy}%</div>
                <div>{session.wordCount}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>No recent sessions found.</div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>      
      <div className={styles.tabsContainer}>
        <div className={styles.tabList}>
          <button
            className={`${styles.tabButton} ${activeTab === 'modes' ? styles.active : ''}`}
            onClick={() => setActiveTab('modes')}
          >
            Learning Modes
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'stats' ? styles.active : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            Statistics
          </button>
        </div>
        
        <div className={styles.tabContent}>
          {activeTab === 'modes' && renderModes()}
          {activeTab === 'stats' && renderStats()}
        </div>
      </div>
    </div>
  );
};

export default VocabModes; 